import type { CostCenters } from "@/apis/gql/types";
import { useDeleteCostCenters } from "@/queries/cost-centers/delete";
import { useGetAllCostCenters } from "@/queries/cost-centers/fetch";
import {
  type ICostCenterUpdate,
  useUpdateManyCostCenters,
} from "@/queries/cost-centers/update";
import React, { createContext, useEffect, useState } from "react";

interface CostCenterDataProviderProps {
  children: React.ReactNode; // This type is appropriate for children
}

interface CostCentersContextProps {
  loading: boolean;
  costCenters: CostCenters[];
  refetch: () => Promise<any>;
  updateCostCenters: ({
    variables,
  }: {
    variables: ICostCenterUpdate;
  }) => Promise<any>;
  deleteCostCenters: (costCenterid: string[]) => Promise<any>;
  createCostCenter: (variables: Partial<CostCenters>) => Promise<any>;
}

const CostCenterContext = createContext<CostCentersContextProps | undefined>(
  undefined,
);

export const CostCenterDataProvider: React.FC<CostCenterDataProviderProps> = ({
  children,
}) => {
  const {
    costCenters: data,
    refetchCostCenters,
    loadingCostCenters: loading,
  } = useGetAllCostCenters();
  const [costCenters, setCostCenters] = useState<CostCenters[]>([]);
  const { updateManyCostCenters } = useUpdateManyCostCenters();
  const deleteCostCenters = useDeleteCostCenters();
  const handleDeleteCostCenters = async (costCenterIds: string[]) => {
    const c = costCenters.filter(
      (c) => !costCenterIds.includes(c.costCenterId),
    );
    setCostCenters([...c]);
    await deleteCostCenters(costCenterIds);
  };

  useEffect(() => {
    if (data) {
      setCostCenters(data);
    }
  }, [data]);

  const handleUpdateCostCenters = async ({
    variables,
  }: {
    variables: ICostCenterUpdate;
  }) => {
    const updatedCostCenters = [...costCenters];
    for (const uc of variables.costCenterIds) {
      const updateCostCentersIndx = updatedCostCenters.findIndex(
        (c) => c.costCenterId === uc,
      );
      if (updateCostCentersIndx !== -1) {
        updatedCostCenters[updateCostCentersIndx] = {
          ...updatedCostCenters[updateCostCentersIndx],
          ...variables.updatedFields,
        };
      }
    }
    setCostCenters(structuredClone(updatedCostCenters));
    await updateManyCostCenters(variables);
  };

  const handleCreateCostCenter = async (variables: Partial<CostCenters>) => {
    // const res = await createAccount({ variables });
    await refetchCostCenters();
    // return res;
  };

  return (
    <CostCenterContext.Provider
      value={{
        costCenters: costCenters ?? [],
        loading,
        refetch: refetchCostCenters,
        updateCostCenters: handleUpdateCostCenters,
        deleteCostCenters: handleDeleteCostCenters,
        createCostCenter: handleCreateCostCenter,
      }}
    >
      {children}
    </CostCenterContext.Provider>
  );
};

export function useCostCentersDataContext() {
  const context = React.useContext(CostCenterContext);
  if (context === undefined) {
    throw new Error(
      "useCostCentersContext must be used within a CostCentersDataProvider",
    );
  }
  return context;
}
